import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { t } from '../i18n/lingua'

/**
 * Il contenitore delle tabelle larghe: quando le colonne non entrano, **si vede**
 * che la tabella continua. Un'ombra sul bordo da cui c'è altro e una freccia
 * che scorre di una schermata, a sinistra e a destra; niente quando tutto entra.
 *
 * Prima c'era solo la barra di scorrimento orizzontale in fondo alla tabella —
 * su un elenco di cinquanta righe, fuori dallo schermo — e un accenno d'ombra
 * solo su mobile: le colonne a destra di «Chiave naturale» non le trovava
 * nessuno. Dentro resta il `.table-wrap` di sempre, quindi gli stili delle
 * tabelle non cambiano.
 */
export function Scorrevole({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [sx, setSx] = useState(false)
  const [dx, setDx] = useState(false)

  const misura = useCallback(() => {
    const el = ref.current
    if (!el) return
    setSx(el.scrollLeft > 2)
    setDx(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }, [])

  useEffect(() => {
    misura()
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const osserva = new ResizeObserver(() => misura())
    osserva.observe(el)
    const tabella = el.querySelector('table')
    if (tabella) osserva.observe(tabella)
    return () => osserva.disconnect()
  })

  function scorri(verso: 1 | -1) {
    const el = ref.current
    if (!el) return
    el.scrollBy({ left: verso * Math.max(200, el.clientWidth * 0.7), behavior: 'smooth' })
  }

  return (
    <div className={`scorrevole${sx ? ' scorrevole--sx' : ''}${dx ? ' scorrevole--dx' : ''}`}>
      {sx && (
        <button type="button" className="scorrevole__freccia scorrevole__freccia--sx"
                onClick={() => scorri(-1)} aria-label={t('Colonne a sinistra')} title={t('Colonne a sinistra')}>
          ‹
        </button>
      )}
      {dx && (
        <button type="button" className="scorrevole__freccia scorrevole__freccia--dx"
                onClick={() => scorri(1)} aria-label={t('Altre colonne a destra')} title={t('Altre colonne a destra')}>
          ›
        </button>
      )}
      <div className="table-wrap" ref={ref} onScroll={misura}>
        {children}
      </div>
    </div>
  )
}
